
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import Header from "./components/Header";
import AppRoutes from "./routes";
import ActivityTracker from "./components/ActivityTracker";
import AnnouncementPopup from "./components/AnnouncementPopup";
import { BranchProvider } from "./context/BranchContext";
import api from "./utils/api";
import "./index.css";
import "leaflet/dist/leaflet.css";

// ─── Floating Chat Widget ────────────────────────────────────────────────────

const ENIX_LOGO = "/images/enix_logo.png";

const SUGGESTIONS = [
  { label: "⚡ Yesterday's Energy", prompt: "What was yesterday's energy consumption?" },
  { label: "📈 High Usage", prompt: "Why is my energy consumption high?" },
  { label: "🔝 Peak Demand", prompt: "What is the peak energy for my machines?" },
  { label: "💡 Save Cost", prompt: "How can I save money on electricity?" },
];

const loadSession = () => {
  try {
    const token = localStorage.getItem("token");
    const userRaw = localStorage.getItem("user");
    const user = userRaw ? JSON.parse(userRaw) : null;
    if (!token || !user) return null;
    return { token, user };
  } catch { return null; }
};

const formatTime = (v) => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDayLabel = (v) => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return formatTime(v);
  const days = (now - d) / 86400000;
  if (days < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

function parseInlineMarkdown(text) {
  if (!text) return "";
  const parts = [];
  const regex = /(\*\*(.*?)\*\*|`([^`]+)`|\*([^*]+)\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const precedingText = text.substring(lastIndex, match.index);
    if (precedingText) {
      parts.push(precedingText);
    }
    if (match[2] !== undefined) {
      parts.push(<strong key={match.index} style={{ fontWeight: 700 }}>{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      parts.push(
        <code key={match.index} style={{ background: "rgba(0,0,0,0.06)", padding: "1px 5px", borderRadius: 4, fontFamily: "monospace", fontSize: "0.9em" }}>
          {match[3]}
        </code>
      );
    } else if (match[4] !== undefined) {
      parts.push(<em key={match.index}>{match[4]}</em>);
    }
    lastIndex = regex.lastIndex;
  }

  const remainingText = text.substring(lastIndex);
  if (remainingText) {
    parts.push(remainingText);
  }

  return parts.length > 0 ? parts : text;
}

function renderMarkdown(text) {
  if (!text) return null;

  const lines = String(text).split("\n");
  const elements = [];

  let currentList = [];
  let currentListType = null;
  let currentTable = null;
  let currentParagraph = [];

  const flushParagraph = (key) => {
    if (currentParagraph.length > 0) {
      elements.push(
        <p key={`p-${key}`} style={{ margin: "4px 0", lineHeight: 1.5 }}>
          {currentParagraph.map((line, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <br />}
              {parseInlineMarkdown(line)}
            </React.Fragment>
          ))}
        </p>
      );
      currentParagraph = [];
    }
  };

  const flushList = (key) => {
    if (currentList.length > 0) {
      const Tag = currentListType === "ordered" ? "ol" : "ul";
      elements.push(
        <Tag key={`list-${key}`} style={{ margin: "4px 0", paddingLeft: 18 }}>
          {currentList.map((item, idx) => (
            <li key={idx} style={{ marginBottom: 3 }}>{parseInlineMarkdown(item)}</li>
          ))}
        </Tag>
      );
      currentList = [];
      currentListType = null;
    }
  };

  const flushTable = (key) => {
    if (currentTable) {
      elements.push(
        <div
          key={`table-${key}`}
          className="enix-table-scroll"
          style={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            margin: "8px 0",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            background: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5, textAlign: "left", minWidth: 240 }}>
            <thead>
              <tr style={{ background: "#f0fdf4", borderBottom: "2px solid #bbf7d0" }}>
                {currentTable.headers.map((col, idx) => (
                  <th key={idx} style={{ padding: "6px 8px", color: "#0f766e", fontWeight: 700, whiteSpace: "nowrap", fontSize: 11 }}>
                    {parseInlineMarkdown(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentTable.rows.map((row, rIdx) => (
                <tr key={rIdx} style={{ borderBottom: rIdx !== currentTable.rows.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx} style={{ padding: "5px 8px", color: "#334155", whiteSpace: "nowrap", fontSize: 11.5 }}>
                      {parseInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      currentTable = null;
    }
  };

  const flushAll = (key) => {
    flushParagraph(key);
    flushList(key);
    flushTable(key);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushAll(i);
      const level = headingMatch[1].length;
      const Tag = `h${level}`;
      const fontSize = level === 1 ? 15 : level === 2 ? 14 : level === 3 ? 13 : 12;
      elements.push(
        <Tag key={`h-${i}`} style={{ margin: "8px 0 4px", fontSize, fontWeight: 700, color: "#0f172a" }}>
          {parseInlineMarkdown(headingMatch[2])}
        </Tag>
      );
      continue;
    }

    if (line.startsWith("|")) {
      flushParagraph(i);
      flushList(i);

      const cells = line.split("|").slice(1, -1).map(c => c.trim());

      if (!currentTable) {
        currentTable = { headers: cells, rows: [] };
      } else {
        const isDelimiter = cells.every(c => c.replace(/[:-\s]/g, "") === "");
        if (!isDelimiter) {
          currentTable.rows.push(cells);
        }
      }
      continue;
    }

    if (line.startsWith("- ") || line.startsWith("* ")) {
      flushParagraph(i);
      flushTable(i);
      if (currentListType !== "bullet") {
        flushList(i);
        currentListType = "bullet";
      }
      currentList.push(line.substring(2));
      continue;
    }

    const orderedMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph(i);
      flushTable(i);
      if (currentListType !== "ordered") {
        flushList(i);
        currentListType = "ordered";
      }
      currentList.push(orderedMatch[2]);
      continue;
    }

    if (!line) {
      flushAll(i);
      continue;
    }

    flushList(i);
    flushTable(i);
    currentParagraph.push(line);
  }

  flushAll(lines.length);
  return elements;
}

function FloatingChatWidget() {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [session] = useState(() => loadSession());

  // Chat state - preserved across opens/closes and tab switches
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [activeConvId, setActiveConvId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [transLoading, setTransLoading] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const reqRef = useRef(0);
  const location = useLocation();

  // Close chatbot window whenever route changes
  useEffect(() => {
    setOpen(false);
    setShowHistory(false);
  }, [location.pathname, location.search]);

  // Also close chatbot window when sub-tab changes via custom event
  useEffect(() => {
    const handleTabChange = () => {
      setOpen(false);
      setShowHistory(false);
    };
    window.addEventListener("enviz-tab-change", handleTabChange);
    return () => window.removeEventListener("enviz-tab-change", handleTabChange);
  }, []);

  const username = useMemo(() =>
    localStorage.getItem("username") || session?.user?.username || "demo",
    [session]);

  const authConfig = useMemo(() =>
    ({ headers: { Authorization: `Bearer ${session?.token}` } }),
    [session]);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Open chatbot where user left it
  const handleOpen = useCallback(() => {
    setShowHistory(false);
    setOpen(true);
  }, []);

  // Fetch conversation list
  const fetchHistory = useCallback(async () => {
    if (!session?.token) return;
    setHistLoading(true);
    try {
      const res = await api.get("/chat/conversations", authConfig);
      const rows = Array.isArray(res.data) ? res.data : res.data?.conversations || [];
      setConversations(
        rows
          .filter(r => r?.conversation_id)
          .map(r => ({ id: String(r.conversation_id), title: r.title || "Untitled", last: r.last_message_at || null }))
          .sort((a, b) => new Date(b.last || 0) - new Date(a.last || 0))
      );
    } catch { /* silent */ }
    finally { setHistLoading(false); }
  }, [session?.token, authConfig]);

  // Load a past conversation
  const loadConversation = useCallback(async (id) => {
    if (!session?.token) return;
    const reqId = ++reqRef.current;
    setActiveConvId(id);
    setShowHistory(false);
    setTransLoading(true);
    setMessages([]);
    try {
      const res = await api.get(`/chat/conversations/${encodeURIComponent(id)}`, {
        ...authConfig, params: { limit: 50 }
      });
      if (reqId !== reqRef.current) return;
      const rows = Array.isArray(res.data) ? res.data : res.data?.messages || [];
      setMessages(rows.map((r, i) => ({
        id: r.message_id ? `srv-${r.message_id}` : `h-${i}`,
        text: typeof r.content === "string" ? r.content : String(r.content ?? ""),
        isBot: r.role === "assistant",
        time: r.created_at || new Date().toISOString(),
      })));
    } catch { /* silent */ }
    finally { if (reqId === reqRef.current) setTransLoading(false); }
  }, [session?.token, authConfig]);

  const handleShowHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  const handleNewChat = () => {
    reqRef.current++;
    setMessages([]);
    setInput("");
    setActiveConvId(null);
    setSending(false);
    setShowHistory(false);
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    const text = input.trim();
    if (!text || !session?.token || sending) return;

    setSending(true);
    const userMsg = { id: `u-${Date.now()}`, text, isBot: false, time: new Date().toISOString() };
    const botId = `b-${Date.now()}`;
    const botPlaceholder = { id: botId, text: "", isBot: true, status: "thinking", time: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg, botPlaceholder]);
    setInput("");

    try {
      const res = await api.post("/chat", {
        username,
        question: text,
        conversation_id: activeConvId || undefined,
      }, authConfig);

      const convId = res.data.conversation_id ? String(res.data.conversation_id) : null;
      if (convId && !activeConvId) setActiveConvId(convId);

      setMessages(prev => prev.map(m => m.id === botId
        ? { ...m, text: res.data.response || "Sorry, no response.", status: "done" }
        : m
      ));
    } catch {
      setMessages(prev => prev.map(m => m.id === botId
        ? { ...m, text: "Sorry, I couldn't reach the AI agent. Please try again.", status: "error" }
        : m
      ));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleSuggestion = (prompt) => {
    setInput(prompt);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const showSuggestions = messages.length === 0 && !transLoading;

  return (
    <>
      {/* ── FAB Button ── visible when closed ── */}
      {!open && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9001 }}>
          <div
            onClick={handleOpen}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              background: hovered ? "#1BA08E" : "#fff",
              border: "2px solid #1BA08E",
              borderRadius: 32,
              padding: "7px 16px 7px 8px",
              cursor: "pointer",
              boxShadow: "0 4px 20px rgba(27,160,142,0.28)",
              transition: "all 0.25s ease",
              animation: "enix-label-in 0.4s ease",
            }}
          >
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e6f6f3", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", border: "1.5px solid #a7f3d0" }}>
              <img src={ENIX_LOGO} alt="Enix" style={{ width: 24, height: 24, objectFit: "contain" }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: hovered ? "#fff" : "#1BA08E", whiteSpace: "nowrap", letterSpacing: "0.01em", transition: "color 0.25s" }}>
              Ask Enix
            </span>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 0 2px rgba(34,197,94,0.3)", animation: "enix-pulse 2s infinite", flexShrink: 0 }} />
          </div>
        </div>
      )}

      {/* ── Chat Panel ── */}
      <div style={{
        position: "fixed",
        bottom: 0, right: 0,
        width: "clamp(320px, 90vw, 440px)",
        height: "clamp(400px, 80vh, 680px)",
        zIndex: 9000,
        background: "#fff",
        borderTopLeftRadius: 20,
        borderTopRightRadius: 0,
        boxShadow: "0 -4px 40px rgba(0,0,0,0.16)",
        border: "1px solid #e2e8f0",
        borderBottom: "none",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transform: open ? "translateY(0)" : "translateY(110%)",
        transition: "transform 0.38s cubic-bezier(0.4,0,0.2,1)",
        pointerEvents: open ? "all" : "none",
      }}>

        {/* Header Bar */}
        <div style={{
          background: "linear-gradient(135deg, #1BA08E 0%, #0d7a6b 100%)",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}>
          {/* Logo with contain fit so full image is visible */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#fff", border: "2px solid rgba(255,255,255,0.5)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <img src={ENIX_LOGO} alt="Enix" style={{ width: 30, height: 30, objectFit: "contain" }} />
            </div>
            <span style={{ position: "absolute", bottom: 1, right: 1, width: 10, height: 10, borderRadius: "50%", background: "#22c55e", border: "2px solid #0d7a6b", animation: "enix-pulse 2s infinite" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#fff", letterSpacing: "0.01em" }}>Enix</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 1 }}>AI Energy Assistant · Online</div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {/* History button */}
            <button
              onClick={handleShowHistory}
              title="Chat history"
              style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </button>
            {/* New chat button (Add Icon) */}
            <button
              onClick={handleNewChat}
              title="New chat"
              style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            {/* Close button */}
            <button
              onClick={() => setOpen(false)}
              title="Close chat"
              style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* History Slide Panel */}
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "#fff",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          transform: showHistory ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}>
          <div style={{ background: "linear-gradient(135deg, #1BA08E 0%, #0d7a6b 100%)", padding: "14px 16px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <button onClick={() => setShowHistory(false)} title="Back to chat" style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span style={{ flex: 1, fontWeight: 700, fontSize: 15, color: "#fff" }}>Chat History</span>
            <button onClick={handleNewChat} title="New chat" style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 8, padding: "5px 12px", cursor: "pointer", color: "#fff", fontWeight: 700, fontSize: 12 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Chat
            </button>
            <button onClick={() => setOpen(false)} title="Close chat" style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 10px" }}>
            {histLoading ? (
              <div style={{ textAlign: "center", padding: 32, color: "#64748b", fontSize: 13 }}>Loading…</div>
            ) : conversations.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: "#94a3b8", fontSize: 13 }}>No past chats yet.</div>
            ) : (
              conversations.map(conv => (
                <button key={conv.id} onClick={() => loadConversation(conv.id)}
                  style={{
                    width: "100%", textAlign: "left", display: "flex", flexDirection: "column", gap: 3,
                    padding: "10px 12px", margin: "2px 0", border: "none", borderRadius: 10,
                    background: conv.id === activeConvId ? "#e6f6f3" : "transparent",
                    cursor: "pointer", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (conv.id !== activeConvId) e.currentTarget.style.background = "#f1f5f9"; }}
                  onMouseLeave={e => { if (conv.id !== activeConvId) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ fontSize: 13, fontWeight: conv.id === activeConvId ? 700 : 500, color: conv.id === activeConvId ? "#0f4039" : "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {conv.title}
                  </span>
                  {conv.last && (
                    <span style={{ fontSize: 11, color: "#94a3b8" }}>{formatDayLabel(conv.last)}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 12px 0", display: "flex", flexDirection: "column", gap: 12, background: "#f8fafc" }}>
          {/* Welcome banner when empty */}
          {showSuggestions && (
            <div style={{ textAlign: "center", paddingTop: 8, paddingBottom: 4 }}>
              <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg, #e6f6f3 0%, #d1fae5 100%)", marginBottom: 10, border: "2px solid #a7f3d0" }}>
                <img src={ENIX_LOGO} alt="Enix" style={{ width: 46, height: 46, objectFit: "contain" }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 18, color: "#0f172a", marginBottom: 4 }}>Hi, I'm Enix! 👋</div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14, lineHeight: 1.5 }}>Your AI energy assistant. Ask me anything about your energy data.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => handleSuggestion(s.prompt)}
                    style={{ fontSize: 11, fontWeight: 600, padding: "6px 12px", borderRadius: 20, border: "1.5px solid #1BA08E", background: "#fff", color: "#1BA08E", cursor: "pointer", transition: "all 0.18s", whiteSpace: "nowrap" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#1BA08E"; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#1BA08E"; }}
                  >{s.label}</button>
                ))}
              </div>
            </div>
          )}

          {transLoading && (
            <div style={{ textAlign: "center", padding: 24, color: "#64748b", fontSize: 13 }}>Loading conversation…</div>
          )}

          {messages.map(msg => (
            <div key={msg.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", flexDirection: msg.isBot ? "row" : "row-reverse", width: "100%", boxSizing: "border-box" }}>
              {msg.isBot && (
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e6f6f3", border: "1px solid #a7f3d0", flexShrink: 0, marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  <img src={ENIX_LOGO} alt="Enix" style={{ width: 22, height: 22, objectFit: "contain" }} />
                </div>
              )}
              <div style={{
                maxWidth: msg.isBot ? "94%" : "82%",
                minWidth: 0,
                flex: "0 1 auto",
                overflow: "hidden",
                background: msg.isBot ? "#fff" : "linear-gradient(135deg, #1BA08E 0%, #0d7a6b 100%)",
                color: msg.isBot ? "#0f172a" : "#fff",
                borderRadius: msg.isBot ? "4px 16px 16px 16px" : "16px 4px 16px 16px",
                padding: "10px 14px",
                fontSize: 13,
                lineHeight: 1.55,
                boxShadow: msg.isBot ? "0 1px 4px rgba(0,0,0,0.07)" : "0 2px 8px rgba(27,160,142,0.3)",
                border: msg.isBot ? "1px solid #e2e8f0" : "none",
                wordBreak: "break-word",
              }}>
                {msg.isBot && msg.status === "thinking" ? (
                  <span style={{ display: "flex", gap: 4, alignItems: "center", padding: "2px 0" }}>
                    {[0, 0.2, 0.4].map((d, i) => (
                      <span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#1BA08E", display: "inline-block", animation: `enix-bounce 1.2s ${d}s infinite` }} />
                    ))}
                  </span>
                ) : msg.isBot ? (
                  renderMarkdown(msg.text)
                ) : (
                  <span style={{ whiteSpace: "pre-wrap" }}>{msg.text}</span>
                )}
                <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4, textAlign: msg.isBot ? "left" : "right" }}>
                  {formatTime(msg.time)}
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input Area */}
        <div style={{ padding: "10px 12px 14px", background: "#fff", borderTop: "1px solid #f1f5f9", flexShrink: 0 }}>
          <form onSubmit={handleSend} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Enix anything…"
              rows={1}
              style={{
                flex: 1,
                border: "1.5px solid #e2e8f0",
                borderRadius: 12,
                padding: "10px 14px",
                fontSize: 13,
                fontFamily: "inherit",
                resize: "none",
                outline: "none",
                lineHeight: 1.5,
                maxHeight: 80,
                overflowY: "auto",
                transition: "border-color 0.2s, box-shadow 0.2s",
                background: "#f8fafc",
              }}
              onFocus={e => { e.target.style.borderColor = "#1BA08E"; e.target.style.boxShadow = "0 0 0 3px rgba(27,160,142,0.12)"; e.target.style.background = "#fff"; }}
              onBlur={e => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; e.target.style.background = "#f8fafc"; }}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              style={{
                width: 40, height: 40,
                borderRadius: 12,
                background: (!sending && input.trim()) ? "linear-gradient(135deg, #1BA08E 0%, #0d7a6b 100%)" : "#e2e8f0",
                border: "none", cursor: (!sending && input.trim()) ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: (!sending && input.trim()) ? "#fff" : "#94a3b8",
                transition: "all 0.2s",
                flexShrink: 0,
                boxShadow: (!sending && input.trim()) ? "0 3px 12px rgba(27,160,142,0.35)" : "none",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </form>
          <div style={{ fontSize: 10, color: "#cbd5e1", textAlign: "center", marginTop: 8 }}>Powered by EnFo AI · Enix</div>
        </div>
      </div>

      {/* Global keyframe animations injected once */}
      <style>{`
        @keyframes enix-label-in {
          from { opacity: 0; transform: translateY(8px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes enix-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.4); }
          50%       { box-shadow: 0 0 0 5px rgba(34,197,94,0); }
        }
        @keyframes enix-bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40%            { transform: scale(1); opacity: 1; }
        }
        .enix-table-scroll::-webkit-scrollbar {
          height: 4px;
        }
        .enix-table-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .enix-table-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .enix-table-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </>
  );
}

// ─── App Shell ───────────────────────────────────────────────────────────────

function AppShell() {
  const location = useLocation();
  const showHeader = location.pathname !== "/login";
  const showChatbot = location.pathname !== "/login";

  return (
    <div className="app-shell">
      <ActivityTracker />
      {showHeader && <Header />}
      <AnnouncementPopup />
      <div className="app-body">
        <main className="app-content">
          <AppRoutes />
        </main>
      </div>
      {showChatbot && <FloatingChatWidget />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <BranchProvider>
        <AppShell />
      </BranchProvider>
    </BrowserRouter>
  );
}

export default App;
